import { useEffect, useState, type CSSProperties } from "react";
import { ApiError } from "@keydials/api-client";
import { api, getAccessToken } from "../../auth/AuthContext";
import { Icon } from "../Icon";
import { announceCalendarsSynced, syncOutcome } from "../../lib/calendarSync";

/**
 * KEY-817: "Sync calendars" in the account menu — fetches every one of my connected calendars now, rather than
 * waiting for the 15-minute sweep, and tells an open dial to read its days again. Only there for someone with a
 * calendar connected: the menu asks when it opens (the list is one small read), so nobody else ever sees it.
 * The menu stays open while it runs, so the result can be read.
 */
export function SyncCalendarsMenuItem({ style }: { style: CSSProperties }) {
  const [hasCalendars, setHasCalendars] = useState(false);
  const [state, setState] = useState<{ busy: boolean; message: string | null }>({ busy: false, message: null });

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let live = true;
    api.calendarConnections(token)
      .then((list) => { if (live) setHasCalendars(!!list.enabled && (list.connections?.length ?? 0) > 0); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (!hasCalendars) return null;

  async function sync() {
    const token = getAccessToken();
    if (!token || state.busy) return;
    setState({ busy: true, message: null });
    try {
      const result = await api.syncAllCalendars(token);
      setState({ busy: false, message: syncOutcome(result.connections) });
      announceCalendarsSynced();
    } catch (e) {
      setState({
        busy: false,
        message: e instanceof ApiError && e.status === 429 ? "Just synced — try again in a moment" : "Couldn't sync — try again",
      });
    }
  }

  return (
    <button onClick={() => void sync()} disabled={state.busy} className="kd-row-hover" style={style} aria-live="polite">
      <Icon name="calendar" size={16} style={{ color: "var(--text-faint)" }} />
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
        {state.busy ? "Syncing calendars…" : "Sync calendars"}
        {state.message && <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{state.message}</span>}
      </span>
    </button>
  );
}
